import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Checkbox,
  Text,
} from "@chakra-ui/react";
import { CallbackUrlContext, MultiForm } from "./MultiForm";
import { navigate } from "gatsby";
import { queryTasks } from "../utils";
import { stringify } from "querystring";

type PromptContentProps = {
  children: (args: () => void) => JSX.Element;
};

type TaskInputType = Record<string, any>;

export type UnsavedTasks = Record<string, true | string>;

export function PromptContent({ children }: PromptContentProps) {
  const [signUp, setSignUp] = useState<boolean | null>(null);
  const onSignUp = useCallback(() => setSignUp(true), []);

  const query = useMemo(() => {
    const { completeTasks } = queryTasks();

    const tasks = completeTasks.reduce<UnsavedTasks>((acc, task) => {
      const taskObj: TaskInputType = { ...task.dataset };

      if (taskObj.task) {
        // TODO: save the code challenge value as a string here
        const nextTask: UnsavedTasks = {
          [taskObj.task]: true,
        };

        return {
          ...acc,
          ...nextTask,
        };
      }

      return acc;
    }, {});

    return stringify({ tasks: JSON.stringify(tasks) });
  }, []);

  if (signUp === null) {
    return children(onSignUp);
  }

  return (
    <CallbackUrlContext.Provider value={{ query: `?${query}` }}>
      <MultiForm isSignUp={signUp} onChange={setSignUp} />
    </CallbackUrlContext.Provider>
  );
}

type PromptProps = {
  navigateTo: string | null;
  setNoPrompt: Dispatch<SetStateAction<boolean | undefined>>;
  onClose: () => void;
};

export default function Prompt({
  navigateTo,
  setNoPrompt,
  onClose,
}: PromptProps) {
  const signInRef = useRef(null);
  const [savePref, setSavePref] = useState(false);

  return (
    <AlertDialog
      size="lg"
      isOpen={Boolean(navigateTo)}
      leastDestructiveRef={signInRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogCloseButton />
          <PromptContent>
            {(onSignUp) => (
              <>
                <AlertDialogHeader>You have unsaved progress</AlertDialogHeader>
                <AlertDialogBody>
                  <Text mb="4">
                    To save your progress, log in to Odyssey. If you don&apos;t,
                    your progress will not be saved. Are you sure you want to
                    leave this page?
                  </Text>
                  <Checkbox
                    isChecked={savePref}
                    onChange={(event) => setSavePref(event.target.checked)}
                  >
                    Don&apos;t ask me this again
                  </Checkbox>
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button
                    onClick={() => {
                      if (navigateTo) {
                        navigate(navigateTo);
                      }

                      if (savePref) {
                        setNoPrompt(true);
                      }
                    }}
                  >
                    Yes, leave page
                  </Button>
                  <Button
                    ref={signInRef}
                    colorScheme="indigo"
                    onClick={onSignUp}
                    ml="3"
                  >
                    Sign up
                  </Button>
                </AlertDialogFooter>
              </>
            )}
          </PromptContent>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
